import React, { useMemo } from 'react';
import type { Theme } from '@mui/material';
import { Box, Tooltip, Typography } from '@mui/material';
import { parseISO, isValid } from 'date-fns';
import { secondsToDhms } from 'c-lib';
import { useDateUtils } from 'c-hooks';
import type { EntityListColumn } from 'c-pagination';
import type { PopDisplays_JobStatus } from 'c-sdk';
import { deriveJobState } from './lib';

/**
 * The white overlays are tuned for the pastel light mode row tints. Dark mode rows are a translucent
 * wash on a dark page, where the same whites would glare, so they're toned down there.
 */
const tintedRowAlpha = (theme: Theme, lightModeAlpha: number) =>
    theme.palette.mode === 'dark' ? lightModeAlpha / 3 : lightModeAlpha;

const maxDurationParts = 3;

/**
 * Wide enough for the longest label the three-part format can produce — `12d 5h 25m` — so the
 * column never resizes to fit its contents, whether or not a page happens to contain an hours-long
 * job. Every row therefore gets the same track for the bar to be measured against.
 */
const DURATION_CELL_WIDTH = 104;

const parseDate = (value?: string) => {
    if (!value) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
};

/**
 * The baseline this row can be measured against, or null when there isn't one.
 *
 * A median of zero means the task routinely finishes inside a second — Sitelist imports do — and
 * "0s" is not something a duration can be a proportion of. Treating it as a baseline produced a
 * bar on every such row and a tooltip reading "typically takes 0s", which says nothing.
 */
const baselineFor = (typical: number | null | undefined) =>
    typical != null && typical > 0 ? typical : null;

/**
 * How full the bar is.
 *
 * A full bar means "this has taken as long as this kind of task usually takes" — so it reads as
 * progress towards the expected finish, and a job still running with a full bar is overdue. That
 * only works against a per-family baseline: a full bar at 30 seconds is right for a Campaign Sync
 * and absurd for a Talon import, which normally runs for nineteen minutes.
 *
 * With no baseline there is no bar. An absolute fallback scale would draw one that looked like the
 * others but meant something different, which is worse than drawing nothing.
 */
const washPercent = (seconds: number, baseline: number) =>
    Math.min(Math.max(seconds / baseline, 0), 1) * 100;

const formatDuration = (seconds: number, parts: number = maxDurationParts) => {
    const { d, h, m, s } = secondsToDhms(seconds);

    const label = [
        { label: 'd', value: d },
        { label: 'h', value: h },
        { label: 'm', value: m },
        { label: 's', value: s },
    ]
        .filter(part => part.value > 0)
        .slice(0, parts)
        .map(part => `${part.value}${part.label}`)
        .join(' ');

    // Dropping every zero part leaves nothing at all for a sub-second job, and Sitelist imports
    // finish inside a second routinely — they were rendering an empty cell.
    return label === '' ? '0s' : label;
};

const timestampLabels = ['Created', 'Started', 'Completed'] as const;

const emptyDurationSx = {
    display: 'flex',
    alignItems: 'center',
    minHeight: 30,
    px: 1,
    width: DURATION_CELL_WIDTH,
    boxSizing: 'border-box',
    justifyContent: 'center',
    color: 'text.disabled',
} as const;

/**
 * The duration, with the cell itself acting as the bar.
 *
 * The Status chip sits alongside, and is what makes the figure unambiguous: "43s Completed" is a
 * measured run, "22m Running" is elapsed so far, "3h Queued" is time spent waiting. That pairing is
 * why nothing here needs a glyph of its own to mark elapsed-versus-measured.
 */
const DurationCell: EntityListColumn<PopDisplays_JobStatus>['RenderCell'] = ({ entity }) => {
    const { created_at: createdAt, started_at: startedAt, completed_at: completedAt } = entity;
    const { formatDateString, timeFormatString, dayMonthYearFormat } = useDateUtils();

    // Deliberately not memoised: a running job's duration is measured against "now", so caching it
    // against the timestamps would freeze the elapsed time of every in-flight task.
    //
    // A job that has NOT started measures from created_at, and that is the point — the Queued chip
    // says the figure is time spent waiting.
    //
    // A FINISHED job measures from started_at or not at all. Falling back to created_at there
    // silently switches the number from execution time to queue wait PLUS execution under the same
    // "Duration" label, and it is not a rounding error: across ~1,900 such rows, p50 107s / p90
    // 220s / max 1448s, each inflated by however long it happened to queue. Unknown is the honest
    // answer, and the tooltip's Created/Started/Completed rows show why it is unknown.
    const startedDate = parseDate(startedAt);
    const completedDate = parseDate(completedAt);
    const origin = startedDate ?? (completedDate == null ? parseDate(createdAt) : null);
    const end = completedDate ?? new Date();

    const seconds = useMemo(() => {
        if (origin == null) return null;
        return Math.abs(end.getTime() - origin.getTime()) / 1000;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origin?.getTime(), end.getTime()]);

    // Succeeded, failed and stuck rows are flooded with a hardcoded tint whatever the theme, so the
    // wash cannot simply take a themed colour on them.
    const state = deriveJobState(entity);
    const onTintedRow = state === 'succeeded' || state === 'failed' || state === 'stuck';

    if (seconds == null) return null;

    const finished = completedAt != null;
    const started = startedAt != null;
    const baseline = baselineFor(entity.typical_seconds);

    /**
     * Progress-bar semantics: a finished job is 100% done however long it took, and needs no
     * baseline to say so. Only a job still running has to be estimated, which is the one case that
     * needs to know how long its kind usually runs — and with no baseline it gets no bar at all
     * rather than one that looks like the others and means something else.
     */
    const width = finished ? 100 : baseline == null ? null : washPercent(seconds, baseline);

    /**
     * Only for a job actually running, and only where its family has a baseline. A finished job has
     * no time remaining, an unstarted one has no work in progress to predict the end of, and a
     * family with no baseline has nothing to predict from.
     */
    const remaining = !finished && baseline != null ? baseline - seconds : null;

    // Slow is a comparison against this task family's own recent runs, made server-side. It can't
    // be carried by colour — the row tint stays, and an amber figure has too little contrast on
    // #90ee90 — so the weight carries it and the tooltip explains it.
    const isSlow = entity.is_slow === true;

    // The stored timestamps are UTC with an offset suffix. Shown raw they are both unreadable and
    // wrong for the reader, so they go through the same formatter the rest of the page uses, which
    // renders in local time.
    const detail = (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 1.25 }}>
            {timestampLabels.map((label, index) => {
                const date = parseDate([createdAt, startedAt, completedAt][index]);

                return (
                    <React.Fragment key={label}>
                        <Box sx={{ opacity: 0.7 }}>{label}</Box>
                        <Box>
                            {date == null
                                ? '—'
                                : formatDateString({
                                      date,
                                      dateFormatString: dayMonthYearFormat,
                                      removeTime: false,
                                      timeFormat: timeFormatString,
                                  })}
                        </Box>
                    </React.Fragment>
                );
            })}
            {baseline != null && (
                <Box sx={{ gridColumn: '1 / -1', mt: 0.75 }}>
                    {isSlow ? 'Slower than usual — this' : 'This'} kind of task typically takes{' '}
                    {formatDuration(baseline)}
                    {remaining != null &&
                        (remaining > 0
                            ? `, so about ${formatDuration(remaining)} left`
                            : `, so this is ${formatDuration(Math.abs(remaining))} overdue`)}
                </Box>
            )}
        </Box>
    );

    /**
     * A job that has neither started nor finished has no duration to report. The only elapsed time
     * it has is how long it has been queued, and showing that under a column headed "Duration"
     * claims it ran for 28 seconds when nothing has run at all. The Status chip beside it already
     * says it is waiting, and the tooltip still carries its Created time.
     *
     * A job that finished without ever starting is different and does show a figure: Fargate
     * frequently writes no start, so created-to-completed is the only measure available for roughly
     * one row in ten, and it did genuinely run.
     */
    if (!finished && !started) {
        return (
            <Tooltip title={detail}>
                <Box sx={emptyDurationSx}>—</Box>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={detail}>
            <Box
                sx={theme => ({
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 30,
                    px: 1,
                    borderRadius: 0.75,
                    // Fixed, because the bar is drawn across this box: left to size itself to its
                    // own label, 100% would mean a different number of pixels on a "5s" row than on
                    // a "1h 11m 41s" one, and the bars would not compare across rows. It also stops
                    // the column stepping about as a running job ticks over.
                    width: DURATION_CELL_WIDTH,
                    boxSizing: 'border-box',
                    // An empty track is what makes it read as a progress bar rather than a
                    // highlighted cell — there is visibly something left to fill.
                    bgcolor:
                        width == null
                            ? 'transparent'
                            : onTintedRow
                            ? `rgba(255, 255, 255, ${tintedRowAlpha(theme, 0.22)})`
                            : theme.palette.action.hover,
                })}
            >
                {width != null && (
                    <Box
                        aria-hidden
                        sx={theme => ({
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${width}%`,
                            // Lighten the tinted rows rather than darken them: darkening a
                            // saturated green gives a muddy olive that reads as a selected cell.
                            bgcolor: onTintedRow
                                ? `rgba(255, 255, 255, ${tintedRowAlpha(theme, 0.72)})`
                                : theme.palette.action.selected,
                            transition: 'width 600ms ease-out',
                            // Barber-pole stripes, and only while the job is actually in flight, so
                            // movement on the page always means something is still happening.
                            ...(finished
                                ? {}
                                : {
                                      backgroundImage: `repeating-linear-gradient(
                                          45deg,
                                          rgba(255, 255, 255, ${tintedRowAlpha(theme, 0.45)}) 0 6px,
                                          transparent 6px 12px
                                      )`,
                                      backgroundSize: '17px 17px',
                                      animation: 'taskQueueProgressStripes 900ms linear infinite',
                                      '@keyframes taskQueueProgressStripes': {
                                          from: { backgroundPosition: '0 0' },
                                          to: { backgroundPosition: '17px 0' },
                                      },
                                      '@media (prefers-reduced-motion: reduce)': {
                                          animation: 'none',
                                      },
                                  }),
                        })}
                    />
                )}
                <Typography
                    fontSize="small"
                    noWrap
                    fontWeight={isSlow ? 700 : undefined}
                    // Poppins' proportional digits make "41s" wider than "40s", so a running job
                    // would nudge everything beside it once a second without this.
                    sx={{ position: 'relative', fontVariantNumeric: 'tabular-nums' }}
                >
                    {formatDuration(seconds)}
                </Typography>
                {remaining != null && (
                    // Only ever on a row that is still going, so the column does not gain a second
                    // line on the completed rows that make up most of a page.
                    <Typography
                        component="span"
                        noWrap
                        sx={{
                            position: 'relative',
                            fontSize: 11,
                            lineHeight: 1.2,
                            opacity: 0.75,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {/* Two parts, not three: this line sits under the duration in a fixed
                            104px cell, and "1h 11m 41s over" would be clipped. */}
                        {remaining > 0
                            ? `~${formatDuration(remaining, 2)} left`
                            : `${formatDuration(Math.abs(remaining), 2)} over`}
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
};

export default DurationCell;
